import { useState, useEffect, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { isBefore, format } from 'date-fns';
import logger from '@widgets/logger';
import { styled } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { ControlledToaster } from '@components/Toaster';
import { useTranslation, useQueryParams, useLocations } from '@hooks';
import { Trans } from 'react-i18next';
import { reviewsService } from '@services';
import {
    reviewActions,
    replyTemplatesActions,
    keywordsActions,
    accountActions,
    contactsActions,
    labelsActions,
    channelsActions,
} from '@actions';
import ContactDrawer from '@features/ContactDrawer';
import ExportCSV from '@features/ExportCSV';
import { FiltersIntegrationType } from '@constants/integrations';
import { getKeywordsFilterOptions } from '@helpers/filters';
import FiltersPanel from './FiltersPanel';
import Reviews from './Reviews';

const Container = styled('div')({
    '@media (max-width: 1024px)': {
        padding: '16px 16px 0',
    },
});

const MINUTE = 1000 * 60;

const ReviewsPage = () => {
    const { t } = useTranslation();
    const params = useQueryParams();
    const history = useHistory();
    const { companies } = useSelector(state => state.account?.account);
    const { reviews: reviewsList = [], pageToken, loading, newReviewsExist } = useSelector(state => state.reviews);
    const { dataAll: keywords, loading: keywordsLoading } = useSelector(state => state.keywords);
    const language = useSelector(state => state.auth.user?.language);
    const { contact } = useSelector(state => state.contacts);

    const queryParamsReviewId = params.get('reviewId');

    const [filter, setFilter] = useState({
        companies: [],
        integrations: params.get('integrationNames') ? JSON.parse(params.get('integrationNames')) : [],
        timeframe: { start: null, end: null },
        rating: [1, 2, 3, 4, 5],
        keywords: params.get('keyword') ? [params.get('keyword')] : [],
        withoutReplies: params.get('withoutReplies') ? Boolean(params.get('withoutReplies')) : false,
        ignoreKeywords: params.get('keyword') ? false : true,
        reviewId: queryParamsReviewId ? queryParamsReviewId : undefined,
    });
    const [openAlert, setOpenAlert] = useState(false);
    const [filterNumber, setFilterNumber] = useState(0);
    const [filterOpened, setFiltersOpened] = useState(false);

    const { mapLocationsToFormValue } = useLocations();
    const isMobile = useMediaQuery('@media (max-width: 1024px)');
    const dispatch = useDispatch();

    const firstRender = useRef(true);

    useEffect(() => {
        dispatch(keywordsActions.getAll());
        dispatch(replyTemplatesActions.getAll());
        dispatch(labelsActions.get());
        dispatch(channelsActions.get());
    }, [dispatch]);

    const availableCompanies = useMemo(
        () =>
            companies.filter(
                company =>
                    company?.profiles?.length > 0 && company?.profiles.some(p => p.type in FiltersIntegrationType),
            ),
        [companies],
    );

    useEffect(() => {
        if (availableCompanies.length && !keywordsLoading) {
            const integrations = [
                ...new Set(
                    availableCompanies
                        .map(company => company.profiles)
                        .flat()
                        .filter(p => p.type in FiltersIntegrationType)
                        .map(p => p.type),
                ),
            ];
            setFilter(prev => ({
                ...prev,
                integrations,
                companies: mapLocationsToFormValue(availableCompanies),
                keywords: params.get('keyword')
                    ? [params.get('keyword')]
                    : getKeywordsFilterOptions(
                          keywords,
                          availableCompanies.map(item => item.companyId),
                      ).map(kw => kw.value),
            }));
            const queryParamsFilter = [
                params.get('keyword'),
                params.get('withoutReplies'),
                params.get('integrationNames'),
            ];
            setFilterNumber(queryParamsFilter.filter(Boolean).length);
        } else if (!availableCompanies.length && !keywordsLoading) {
            dispatch(reviewActions.resetLoading());
        }
    }, [availableCompanies, keywordsLoading, keywords, dispatch]);

    const currentQuery = useMemo(() => {
        return {
            ...filter,
            companies: filter.companies.length ? filter.companies.map(c => c.id) : [],
            integrationNames: filter.integrations,
            from: filter.timeframe.start ? format(new Date(filter.timeframe.start), 'dd/MMM/yyyy') : null,
            to: filter.timeframe.end ? format(new Date(filter.timeframe.end), 'dd/MMM/yyyy') : null,
        };
    }, [filter]);

    useEffect(() => {
        if (queryParamsReviewId && !firstRender.current) {
            dispatch(reviewActions.getAll({ ...currentQuery, reviewId: queryParamsReviewId }));
            history.replace();
        }
    }, [queryParamsReviewId]);

    useEffect(() => {
        if (currentQuery.companies.length && currentQuery.integrationNames.length && currentQuery.rating.length) {
            if (firstRender.current) {
                dispatch(reviewActions.getAll(currentQuery));
                firstRender.current = false;
            }
        }
    }, [currentQuery, dispatch]);

    useEffect(() => {
        const interval = setInterval(async () => {
            try {
                const newExists = await reviewsService.checkNewExist({ ...currentQuery, reviewId: undefined });
                dispatch(reviewActions.checkNewExistSuccess(newExists));
            } catch (_) {
                logger.error('Failed to check new reviews');
            }
        }, MINUTE);
        return () => clearInterval(interval);
    }, [currentQuery, dispatch]);

    useEffect(() => {
        const feedType = getFeedType();
        if (newReviewsExist && feedType === 2) {
            getNewReviews();
            dispatch(accountActions.get());
        } else if (newReviewsExist) {
            setOpenAlert(true);
        }
    }, [newReviewsExist]);

    const getNewReviews = () => {
        setOpenAlert(false);
        dispatch(reviewActions.resetNewExist());
        dispatch(reviewActions.getAll({ ...currentQuery, reviewId: undefined }));
    };

    const onCloseToaster = () => dispatch(reviewActions.resetNewExist());

    const loadMore = () => {
        if (!pageToken) return;
        dispatch(reviewActions.loadMore(currentQuery, pageToken));
    };

    const onApplyFilters = () => {
        dispatch(reviewActions.getAll({ ...currentQuery, reviewId: undefined }));
    };

    const getFeedType = () => {
        if (!filter.companies.length || !filter.integrations.length || !filter.rating.length) {
            return 1;
        }
        const isScraping = companies
            .filter(c => filter.companies.some(fC => fC.id === c.companyId))
            .map(item => item.profiles)
            .flat()
            .filter(c => filter.integrations.includes(c.type))
            .every(c => isBefore(new Date(c.lastScrapedTime), new Date(2000, 1, 1)));
        if (isScraping && !reviewsList.length && !firstRender.current) {
            return 2;
        }
        if (!reviewsList.length && !firstRender.current) {
            return 3;
        }
        return 0;
    };

    const onClickContactDetails = contactId => {
        if (contactId !== contact?.id) {
            dispatch(contactsActions.getContact(contactId));
        }
        dispatch(contactsActions.setContactDrawer(true));
    };

    const onUpdateContactCallback = () => {
        dispatch(reviewActions.invalidateReviews(currentQuery, null, reviewsList.length));
    };

    return (
        <Container>
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
                <FiltersPanel
                    setFiltersOpened={setFiltersOpened}
                    filtersNumber={filterNumber}
                    filterOpened={filterOpened}
                    keywords={keywords}
                    filter={filter}
                    setFilter={setFilter}
                    type="reviews"
                    setFilterNumber={setFilterNumber}
                    onApplyFilters={onApplyFilters}
                    paramsKeyword={params.get('keyword')}
                />
                <ExportCSV callback={() => reviewsService.exportCSV(currentQuery)} type="reviews" />
            </div>
            <Reviews
                isMobile={isMobile}
                data={reviewsList}
                language={language}
                pageToken={pageToken}
                loading={loading}
                companies={companies}
                fetchData={loadMore}
                feedType={getFeedType()}
                onClickContactDetails={onClickContactDetails}
            />
            <ControlledToaster
                callback={getNewReviews}
                autoHideDuration={null}
                text={
                    <Trans t={t} i18nKey={'ReviewsManage.newReviews'}>
                        <b style={{ textDecoration: 'underline', cursor: 'pointer' }} onClick={getNewReviews}>
                            {{ update: t('ReviewsManage.update') }}
                        </b>
                    </Trans>
                }
                type="info"
                open={openAlert}
                setOpen={setOpenAlert}
                clickClose={onCloseToaster}
            />
            <ContactDrawer onUpdateCallback={onUpdateContactCallback} />
        </Container>
    );
};

export default ReviewsPage;
